import { getState, updateChatState, emit, on } from "../core/state.js";
import { getTheme, getUiFeature } from "../core/configAccessors.js";
import searchIcon from "../assets/icons/search.svg";
import launcherCSS from "../styles/LauncherButton.css";

// Old main.js opened 2s after load, long enough for the page to settle.
const FIRST_VISIT_OPEN_DELAY_MS = 2000;

// Old main.js used a session cookie (`_visitor`, no expiry) so one open
// silenced the auto-open for the rest of the browser visit and came back on
// the next one. A cookie, not sessionStorage: it has to be shared across the
// tabs of one visit, which per-tab storage is not.
const VISIT_COOKIE = "ws_visitor";

function hasVisitOpened() {
  try {
    return document.cookie
      .split("; ")
      .some((row) => row.startsWith(`${VISIT_COOKIE}=`));
  } catch {
    // No cookie access (sandboxed frame): treat as already opened, so the
    // panel can't auto-open on every single page load.
    return true;
  }
}

function markVisitOpened() {
  try {
    // SameSite=None needs Secure, which needs https — on a local http test
    // page that pair is dropped rather than losing the cookie entirely.
    const secure = window.location.protocol === "https:";
    document.cookie = `${VISIT_COOKIE}=1; path=/; ${
      secure ? "Secure; SameSite=None" : "SameSite=Lax"
    }`;
  } catch {
    /* cookies blocked — auto-open simply stays off for this page */
  }
}

// Homepage only, exactly as the old agent had it (`currentPath === "/"`).
// widget/test.html counts too, but only on a local dev host — otherwise the
// rule stays exactly what the winery's visitors get. Same allowance the
// Halleck variant makes for its own auto-open.
const LOCAL_DEV_HOSTS = new Set(["localhost", "127.0.0.1"]);

function isHomePage() {
  const path = window.location.pathname || "";
  if (path === "/" || path === "") return true;
  return (
    LOCAL_DEV_HOSTS.has(window.location.hostname || "") &&
    /\/test\.html$/i.test(path)
  );
}

export class LauncherButton {
  constructor(shadow) {
    this.shadow = shadow;
    this._container = null;
  }

  async init() {
    this._injectStyles();
    this._render();
    // Hide on cart/checkout pages right away (no launcher flash). Auto-open is
    // NOT done here — see maybeAutoOpen().
    if (this._isCartPage() && this._container) this._container.style.display = "none";

    // The visit is "used up" by ANY open, clicked or automatic — old main.js
    // set the _visitor cookie inside initializeChat(), which both paths called.
    on("ws-toggle", ({ isOpen } = {}) => {
      if (isOpen) markVisitOpened();
    });
  }

  // Parity (old main.js autoOpenChat): no launcher / auto-open on a cart or
  // checkout page. Covers both platforms the wineries run: Shopify-style
  // paths (/cart, /checkouts/…) and Commerce7-style ones (/checkout/…,
  // /account?view=cart) — patterns of the platforms, not of one winery.
  _isCartPage() {
    const path = window.location.pathname || "";
    const query = window.location.search || "";
    if (path.startsWith("/cart") || path.startsWith("/checkout")) return true;
    return /[?&]view=(cart|checkout)\b/.test(query);
  }

  // Auto-open the panel for QR/openagent entries, when ui_features.auto_open
  // is set (every page, every load), or — with
  // ui_features.auto_open_first_visit — on the homepage once per browser
  // visit, 2s after load. That last one is old main.js autoOpenChat: the
  // visitor cookie is what stops a refresh from reopening it.
  //
  // Called by init.js AFTER ChatPanel / chat-handler / analytics have
  // subscribed to ws-toggle. Emitting from init() fired before any listener
  // existed: the panel never opened (e.g. Fogarty's add-to-cart reload with
  // ?openagent=true) and chat.isOpen was left stuck at true, so the first
  // launcher click closed instead of opening.
  maybeAutoOpen() {
    if (this._isCartPage()) return;
    const { session, chat } = getState();
    if (chat.isOpen) return;

    if (getUiFeature("auto_open", false) || session.isQR || session.openagent) {
      this._open();
      return;
    }

    if (
      getUiFeature("auto_open_first_visit", false) &&
      isHomePage() &&
      !hasVisitOpened()
    ) {
      // 2s, as the old agent did — the page settles before the panel appears.
      setTimeout(() => {
        if (getState().chat?.isOpen) return;
        this._open();
      }, FIRST_VISIT_OPEN_DELAY_MS);
    }
  }

  _open() {
    updateChatState({ isOpen: true });
    emit("ws-toggle", { isOpen: true });
  }

  _injectStyles() {
    const style = document.createElement("style");
    style.textContent = launcherCSS;
    this.shadow.appendChild(style);
  }

  _render() {
    this._container = document.createElement("div");
    this._container.id = "ws-launcher";

    const btn = document.createElement("button");
    btn.className = "launcher";

    btn.innerHTML = `
      ${searchIcon}
      <span class="label">${getTheme("button_text", "Ask Concierge")}</span>
    `;

    btn.addEventListener("click", () => {
      const state = getState();
      const isOpen = !state.chat.isOpen;
      updateChatState({ isOpen });
      emit("ws-toggle", { isOpen });
    });

    this._container.appendChild(btn);
    this.shadow.appendChild(this._container);
  }
}
